// Email reminder subscriptions — the modal surface (docs/reminders.md), titled
// **Custom Notifications** since 2026-10-14 (owner; *Email Reminders* until then).
// Rendered inside a Dialog off the header's mail icon (Upload's pattern).
// Lists the user's subscriptions (add / edit / pause / remove) and an add/edit
// form whose steps are filtered to what the user can act on. Editing is
// copy-on-write, done in one server call (lib/reminders). The form opens on its
// Type — Scheduled or Triggered (owner, 2026-10-21 — D-391) — and shows the
// fields under it once one is chosen. The form itself is
// `components/CustomNotificationForm`, shared with an Admin's editor on
// Assignments → Notifications (D-454).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Toast,
  ToastTitle,
  useToastController,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { DeleteRegular } from '@fluentui/react-icons'
import { errorMessage } from '../api/errors'
import type { Session } from '../lib/identity'
import type { ReminderSubscription } from '../lib/types'
import { TIMELINE_NODES, TOASTER_ID } from '../lib/constants'
import { loadMyReminders } from '../lib/data'
import {
  stepOptions,
  isActiveSubscription,
  formatSchedule,
  emptyForm,
  rowToForm,
  validateForm,
  saveNewSubscription,
  saveEditedSubscription,
  cancelSubscription,
  remindersTitle,
  type ReminderForm,
} from '../lib/reminders'
import { CustomNotificationForm } from '../components/CustomNotificationForm'
import { DecisionDialog } from '../components/DecisionDialog'
import { StatusDot } from '../components/StatusDot'
import { rowActivationProps, useClickableRowClass, useRoomyTableClass } from '../components/tableDensity'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 },
  // THE LIST IS A TABLE (owner, 2026-10-14: "attempt a redesign of the list of
  // already-established alerts"), as every other list in the app is: one row
  // per notification, the row opens it, and the columns say what it is, when it goes
  // and what it covers without opening it. It was a stack of cards reading
  // "Mon, Tue, Wed, Thu, Fri at 9:00 AM · from 2026-10-14" and "3 steps".
  table: { tableLayout: 'fixed' },
  stepList: { display: 'flex', flexDirection: 'column' },
  rowActions: { display: 'flex', gap: '2px', justifyContent: 'flex-end' },
  // The one action under the list, at the right like every pop-up's buttons.
  listActions: { display: 'flex', justifyContent: 'flex-end' },
  empty: { color: tokens.colorNeutralForeground3, padding: '8px 0' },
})

export function RemindersScreen({
  session,
  onTitleChange,
}: {
  session: Session
  /** The header pop-up's title follows the view (D-454). */
  onTitleChange?: (title: string) => void
}) {
  const s = useStyles()
  const roomy = useRoomyTableClass()
  const clickable = useClickableRowClass()
  const { dispatchToast } = useToastController(TOASTER_ID)

  const [subs, setSubs] = useState<ReminderSubscription[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<'list' | 'form'>('list')
  const [editing, setEditing] = useState<ReminderSubscription | null>(null)
  const [form, setForm] = useState<ReminderForm>(() => emptyForm())
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  /** The edit form's Active/Inactive toggle (D-361). A new notification is Active. */
  const [active, setActive] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)

  const stepOpts = useMemo(() => stepOptions(session), [session])

  useEffect(() => {
    onTitleChange?.(remindersTitle(mode, editing !== null))
  }, [mode, editing, onTitleChange])

  // This screen renders inside App.tsx's own reminders Dialog (never as a
  // standalone page), so the time picker below needs the same fix every
  // other in-dialog Fluent popup on this project needs (app/README.md's
  // Fluent UI v9 gotcha: an unmounted popup "closes as fast as it opens"
  // without an explicit `mountNode`/`inlinePopup`) — resolved the same way
  // `AssociatedEntitiesEditor` resolves its own dialog host, via `.closest`
  // rather than a prop threaded down from App.tsx, since this component has
  // no other reason to know about its parent dialog.
  const rootRef = useRef<HTMLDivElement>(null)
  const [dialogHost, setDialogHost] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setDialogHost(rootRef.current?.closest('[role="dialog"]') as HTMLElement | null)
  }, [mode])

  // The API scopes the list to the caller, so there is nothing to pass.
  const reload = useCallback(async () => {
    setError(null)
    try {
      setSubs(await loadMyReminders())
    } catch (e) {
      setError(errorMessage(e))
      setSubs([])
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const toast = (msg: string) =>
    dispatchToast(<Toast><ToastTitle>{msg}</ToastTitle></Toast>, { intent: 'success' })

  const openNew = () => {
    setEditing(null)
    setForm(emptyForm())
    setActive(true)
    setFormError(null)
    setMode('form')
  }
  const openEdit = (row: ReminderSubscription) => {
    setEditing(row)
    // A step the user no longer holds is not offered, so it is not kept either
    // (D-392); the tick takes it off the stored row too.
    const loaded = rowToForm(row)
    setForm({ ...loaded, steps: loaded.steps.filter((key) => stepOpts.some((o) => o.key === key)) })
    setActive(isActiveSubscription(row))
    setFormError(null)
    setMode('form')
  }

  const save = async () => {
    const err = validateForm(form)
    if (err) {
      setFormError(err)
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      if (editing) await saveEditedSubscription(editing, form, active)
      else await saveNewSubscription(form)
      toast(editing ? 'The custom notification was updated.' : 'The custom notification was created.')
      setMode('list')
      setEditing(null)
      await reload()
    } catch (e) {
      setFormError(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  const rowAction = async (row: ReminderSubscription, fn: () => Promise<void>, msg: string) => {
    setBusyId(row.id)
    try {
      await fn()
      toast(msg)
      await reload()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusyId(null)
    }
  }

  /** THE REMINDER WAITING ON "Are you sure?" (QA wave B, 2026-09-25, B-07;
      `docs/decisions.md` D-171). Remove wrote on one click, and nothing on
      screen brings a removed reminder back. `removingShown` keeps the pop-up's
      words still while it closes — `HoldControl`'s `shown` pattern. */
  const [removing, setRemoving] = useState<ReminderSubscription | null>(null)
  const [removingShown, setRemovingShown] = useState<ReminderSubscription | null>(null)
  const confirmRemove = async () => {
    const row = removing
    if (!row) return
    await rowAction(row, () => cancelSubscription(row), 'The custom notification was removed.')
    setRemoving(null)
  }

  // ---- form view ----
  if (mode === 'form') {
    return (
      <div ref={rootRef}>
        <CustomNotificationForm
          form={form}
          onChange={setForm}
          stepOptions={stepOpts}
          noSteps="You have no steps you can act on."
          triggeredLabel="Notify me when an invoice arrives at"
          dialogHost={dialogHost}
          editing={editing !== null}
          active={active}
          onActiveChange={setActive}
          saving={saving}
          error={formError}
          onCancel={() => setMode('list')}
          onSave={() => void save()}
        />
      </div>
    )
  }

  // ---- list view ----
  const stepLabel = (key: string) => TIMELINE_NODES.find((n) => n.key === key)?.label ?? key
  return (
    <div className={s.root}>
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {subs === null ? (
        <Spinner size="small" label="Loading your custom notifications…" />
      ) : subs.length === 0 ? (
        <Text className={s.empty}>You don’t have any custom notifications yet.</Text>
      ) : (
        <Table size="small" aria-label="Your custom notifications" className={mergeClasses(roomy, s.table)}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: '26%' }}>Title</TableHeaderCell>
              <TableHeaderCell style={{ width: '26%' }}>Schedule</TableHeaderCell>
              <TableHeaderCell>Steps</TableHeaderCell>
              <TableHeaderCell style={{ width: '104px' }}>Status</TableHeaderCell>
              <TableHeaderCell style={{ width: '48px' }} aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {subs.map((row) => {
              const isActive = isActiveSubscription(row)
              const busy = busyId === row.id
              const name = row.title || 'Custom Notification'
              return (
                // The row opens the notification to edit, as a row does on every other list.
                <TableRow key={row.id} className={clickable} {...rowActivationProps(() => openEdit(row), `Edit: ${name}`)}>
                  <TableCell>{name}</TableCell>
                  <TableCell>{formatSchedule(row)}</TableCell>
                  <TableCell>
                    <span className={s.stepList}>
                      {(row.steps ?? []).filter(Boolean).map((key) => (
                        <span key={key}>{stepLabel(key)}</span>
                      ))}
                    </span>
                  </TableCell>
                  {/* THE RULES LISTS' STATUS, a dot and the word (owner,
                      2026-10-14): Active or Inactive, set by the editor's toggle. */}
                  <TableCell>
                    <StatusDot active={isActive} />
                  </TableCell>
                  <TableCell>
                    {/* Its own click only: it does not open the row. Active and
                        Inactive are the editor's toggle now (D-361); the list's
                        Pause and Resume went with that. */}
                    <span className={s.rowActions} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DeleteRegular />}
                        disabled={busy}
                        aria-label="Remove"
                        title="Remove"
                        onClick={() => {
                          setRemovingShown(row)
                          setRemoving(row)
                        }}
                      />
                    </span>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}

      {/* No icon in the button, and at the right (owner, 2026-10-14). Every
          word on this screen says "custom notification", not "reminder" (owner, same
          day). */}
      <div className={s.listActions}>
        <Button appearance="primary" onClick={openNew}>
          Add Custom Notification
        </Button>
      </div>
      <DecisionDialog
        open={removing !== null}
        title="Are you sure?"
        body={`The custom notification “${removingShown?.title || 'Custom Notification'}” will be removed, and its emails will stop.`}
        requireReason={false}
        confirmLabel="Remove"
        submitting={busyId !== null}
        onCancel={() => setRemoving(null)}
        onConfirm={() => void confirmRemove()}
      />
    </div>
  )
}
